/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// The ⋯ "More" dropdown for the profile popover/modal (Figma 4137:10569):
//   • View full profile
//   • Add to group  ›   → side flyout listing the viewer's groups
//   • Block (destructive)
// 253px panel-surface card, 28px rows (radius 8), 16px icons. Purely
// presentational; the host wires the actions + supplies the group list.

import type React from 'react';
import { useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import { ChevronRight, Trash2, UserPlus, UserRound } from 'lucide-react-native';

import { ProfileAvatar } from './ProfileParts';

export interface ProfileMoreMenuGroup {
  readonly id: string;
  readonly name: string;
  readonly avatarUri?: string | undefined;
}

export interface ProfileMoreMenuProps {
  readonly groups: ReadonlyArray<ProfileMoreMenuGroup>;
  /** Escalate to the full profile. Omitted (→ item hidden) when the menu is
   *  already shown FROM the full profile modal, where it would be redundant. */
  readonly onViewFullProfile?: (() => void) | undefined;
  readonly onAddToGroup: (groupId: string) => void;
  readonly onBlock: () => void;
}

const ICON_LIGHT = '#F5F6F8';
const ICON_DANGER = '#FF5353';

function MenuItem({
  icon,
  label,
  danger,
  trailing,
  onPress,
}: {
  icon: React.ReactNode;
  label: string;
  danger?: boolean;
  trailing?: React.ReactNode;
  onPress: () => void;
}): JSX.Element {
  return (
    <Pressable
      accessibilityRole="menuitem"
      accessibilityLabel={label}
      onPress={onPress}
      className="h-7 flex-row items-center gap-3 rounded-lg px-3 active:bg-light-surface-elevated dark:active:bg-dark-surface-elevated"
    >
      {icon}
      <Text
        className={`flex-1 text-[14px] leading-[20px] ${
          danger === true
            ? 'text-[#FF5353]'
            : 'text-light-text-secondary dark:text-dark-text-secondary'
        }`}
        numberOfLines={1}
      >
        {label}
      </Text>
      {trailing}
    </Pressable>
  );
}

export function ProfileMoreMenu({
  groups,
  onViewFullProfile,
  onAddToGroup,
  onBlock,
}: ProfileMoreMenuProps): JSX.Element {
  const [showGroups, setShowGroups] = useState(false);

  const canAddToGroup = groups.length > 0;

  return (
    <View className="w-[253px] rounded-xl border border-light-border-subtle dark:border-dark-border-subtle bg-light-surface-panel dark:bg-dark-surface-panel p-1">
      {onViewFullProfile !== undefined ? (
        <MenuItem
          icon={<UserRound size={16} color={ICON_LIGHT} />}
          label="View full profile"
          onPress={onViewFullProfile}
        />
      ) : null}
      {canAddToGroup ? (
        <MenuItem
          icon={<UserPlus size={16} color={ICON_LIGHT} />}
          label="Add to group"
          trailing={<ChevronRight size={16} color={ICON_LIGHT} />}
          onPress={() => setShowGroups((s) => !s)}
        />
      ) : null}
      {/* The group picker expands inline under "Add to group" (a side flyout
          would clip inside the modal's overflow-hidden surface). */}
      {showGroups
        ? groups.map((g) => (
            <Pressable
              key={g.id}
              accessibilityRole="menuitem"
              accessibilityLabel={`Add to ${g.name}`}
              onPress={() => onAddToGroup(g.id)}
              className="h-7 flex-row items-center gap-3 rounded-lg pl-7 pr-3 active:bg-light-surface-elevated dark:active:bg-dark-surface-elevated"
            >
              <ProfileAvatar name={g.name} uri={g.avatarUri} sizeClassName="w-4 h-4 rounded-[6px]" />
              <Text
                className="flex-1 text-[14px] leading-[20px] text-light-text-secondary dark:text-dark-text-secondary"
                numberOfLines={1}
              >
                {g.name}
              </Text>
            </Pressable>
          ))
        : null}
      <View className="my-1 h-px bg-light-border-subtle dark:bg-dark-border-subtle" />
      <MenuItem
        icon={<Trash2 size={16} color={ICON_DANGER} />}
        label="Block"
        danger
        onPress={onBlock}
      />
    </View>
  );
}
